// src/configs/appResolver.js
import { APPLICATIONS, FORCE_APP } from './initializer';
import { PROFILES } from './profiles';

import { BODY_BACKGROUND, BORDER, GRAY_SCALE, THEME_COLOR } from 'constants/ThemeConstant';
import { onProduction } from 'utils/helpers';

// utils locais
function rgba(hex, opacity = 1) {
  if (!/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    throw new Error('Invalid Hex');
  }
  const decimal = parseInt(hex.substring(1), 16);
  const r = (decimal >> 16) & 255;
  const g = (decimal >> 8) & 255;
  const b = decimal & 255;
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

function getApplicationColorTheme() {
  const domain = (typeof window !== 'undefined' && window.location?.hostname) || '';
  let primaryColor = THEME_COLOR.BLUE;

  if (!onProduction()) {
    if (FORCE_APP?.THEME_COLOR && THEME_COLOR?.[FORCE_APP.THEME_COLOR]) {
      primaryColor = THEME_COLOR?.[FORCE_APP.THEME_COLOR];
    }
  } else {
    for (const app of APPLICATIONS) {
      if (Array.isArray(app?.DOMAINS) && app.DOMAINS.includes(domain)) {
        if (app?.THEME_COLOR && THEME_COLOR?.[app.THEME_COLOR]) {
          primaryColor = THEME_COLOR?.[app.THEME_COLOR];
        }
      }
    }
  }
  return primaryColor;
}

const baseTheme = {
  borderRadius: 8,
  colorPrimary: getApplicationColorTheme(),
  colorSuccess: THEME_COLOR.CYAN,
  colorWarning: THEME_COLOR.GOLD,
  colorError: THEME_COLOR.VOLCANO,
  colorInfo: THEME_COLOR.BLUE,
  colorText: GRAY_SCALE.GRAY,
  colorBorder: BORDER.BASE_COLOR,
  bodyBg: BODY_BACKGROUND,
  controlHeight: 40,
  controlHeightLG: 48,
  controlHeightSM: 36,
  fontFamily: `'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
    'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol',
    'Noto Color Emoji'`,
  fontSizeHeading2: 22,
  fontSizeHeading4: 17,
  colorLink: getApplicationColorTheme(),
};

const APP_PREFIX_PATH = '/app';
const AUTH_PREFIX_PATH = '/auth';
const REDIRECT_URL_KEY = 'redirect';

// *******************************************************************************
// ENVIRONMENT-BASED CONFIG RESOLVER
// *******************************************************************************
// Resolves config values that can be either:
// - A direct value (string, boolean, array, etc.)
// - An object with { homologation: value, production: value }
//
// Example:
//   SELF_REGISTRATION: { homologation: true, production: false }
//   resolves to `true` in homologation mode, `false` in production mode
function resolveEnvConfig(value, isHomologation) {
  // If it's an object with homologation/production keys, resolve based on environment
  if (
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    ('homologation' in value || 'production' in value)
  ) {
    return isHomologation ? value.homologation : value.production;
  }
  // Otherwise return the value as-is
  return value;
}

// *******************************************************************************
// COUNTRY → LOCALE MAP (fixo para app.LOCALE)
// *******************************************************************************
const COUNTRY_TO_LOCALE = {
  br: 'pt-br',
  pt: 'pt-pt',
  us: 'en-us',
  gb: 'en-gb',
  uk: 'en-gb',
  ca: 'en-ca',
  au: 'en-au',
  nz: 'en-nz',
  in: 'en-in',
  de: 'de-de',
  fr: 'fr-fr',
  es: 'es-es',
  mx: 'es-mx',
  ar: 'es-ar',
  cl: 'es-cl',
  it: 'it-it',
  jp: 'ja-jp',
  cn: 'zh-cn',
  tw: 'zh-tw',
};

export function resolveApp() {
  const domain = typeof window !== 'undefined' && window.location ? window.location.hostname : '';

  if (!onProduction() && FORCE_APP) {
    const resolved =
      typeof FORCE_APP === 'string'
        ? APPLICATIONS.find(a => a?.ID === FORCE_APP || a?.NAME === FORCE_APP)
        : FORCE_APP;
    if (resolved) return decorateApp({ ...resolved });
    console.warn(`[appResolver] Forced app "${String(FORCE_APP)}" not found in APPLICATIONS.`);
  }

  const byDomain = APPLICATIONS.find(a => Array.isArray(a?.DOMAINS) && a.DOMAINS.includes(domain));
  if (byDomain) return decorateApp({ ...byDomain });

  console.error(`[appResolver] No application defined for domain: ${domain}`);
  return decorateApp({
    ID: 'fallback',
    NAME: 'Jarvisly',
    LOCALE: 'pt',
    THEME: 'light',
    NAVIGATION_TYPE: 'side',
  });
}

function decorateApp(app) {
  // if (typeof app.PROFILE === 'string') {
  //   if (!PROFILES[app.PROFILE]) {
  //     console.error(`[appResolver] Unknown PROFILE '${app.PROFILE}' in application config`);
  //   } else {
  //     app.PROFILE = PROFILES[app.PROFILE];
  //   }
  // }

  app.BASE_THEME = baseTheme;

  // Locale: 100% derivado do app.LOCALE (sem ler localStorage)
  const country = String(app.LOCALE || 'US').toUpperCase();
  app.locale = COUNTRY_TO_LOCALE[country] || 'en-us';

  app.APP_NAME = app.NAME;
  app.APP_PREFIX_PATH = APP_PREFIX_PATH;
  app.AUTH_PREFIX_PATH = AUTH_PREFIX_PATH;
  app.REDIRECT_URL_KEY = REDIRECT_URL_KEY;

  app.AUTHENTICATED_ENTRY = `${APP_PREFIX_PATH}/dashboard/overview`;
  app.UNAUTHENTICATED_ENTRY = `${AUTH_PREFIX_PATH}/login`;

  // Runtime mode from .env (similar to backend)
  // - 'homologation' connects to homologation backend (ports 8112, 9112, etc.)
  // - 'production' connects to production backend (ports 8012, 9012, etc.)
  const runtimeMode = import.meta.env.VITE_RUNTIME_MODE;
  if (runtimeMode) {
    app.HOMOLOGATION_MODE = runtimeMode === 'homologation';
  } else {
    // Fallback: localhost defaults to homologation, production domains to production
    app.HOMOLOGATION_MODE = !onProduction();
  }

  // Resolve environment-specific configurations
  // These configs can be either direct values or { homologation: x, production: y }
  app.SELF_REGISTRATION = resolveEnvConfig(app.SELF_REGISTRATION, app.HOMOLOGATION_MODE);
  app.SELF_REGISTRATION_MOBILE_VALIDATION = resolveEnvConfig(
    app.SELF_REGISTRATION_MOBILE_VALIDATION,
    app.HOMOLOGATION_MODE
  );
  app.USER_INVITATION_MOBILE_VALIDATION = resolveEnvConfig(
    app.USER_INVITATION_MOBILE_VALIDATION,
    app.HOMOLOGATION_MODE
  );

  // Resolve PWA names per environment
  app.PWA_NAME = resolveEnvConfig(app.PWA_NAME, app.HOMOLOGATION_MODE) || app.NAME;
  app.PWA_SHORT_NAME = resolveEnvConfig(app.PWA_SHORT_NAME, app.HOMOLOGATION_MODE) || app.NAME;

  // Connection target from .env
  // - 'local' connects to localhost (backend running locally)
  // - 'online' connects to vps.jarvisly.com (backend running on VPS)
  const connection = import.meta.env.VITE_CONNECTION;
  if (connection) {
    app.CONNECTION = connection; // 'local' or 'online'
  } else {
    // Fallback: localhost = local, production domains = online
    app.CONNECTION = !onProduction() ? 'local' : 'online';
  }

  app.currentTheme = app.THEME || 'light';
  app.navCollapsed = false;
  app.direction = 'ltr';

  return app;
}

const app = resolveApp();
export default app;
